/*
 * Who reads the page (SPEC decision 23): Hangar is a layer in front of the build services, so clients never see the
 * providers behind it (Expo's build service, Bitrise): they read "Hangar" and neutral words ("the build service",
 * "Built by Hangar"). Platform operators (Malible staff) still see provider names where they help support. During a
 * client preview `GET /me` omits `platformRole`, so the preview gets the client view on its own.
 *
 * One gate for the whole console: `seesProviders(me)` here (pure) and `useSeesProviders()` in `useAudience.ts` (React).
 * API text that may name a provider (event notes, guardrails, error messages) goes through `neutralWording` before a
 * client reads it.
 */
/** The part of `GET /me` the gate reads. */
export type Viewer = { user?: { platformRole?: string | null } | null } | null | undefined;

/** True only for a platform operator: everyone else (clients, a previewed client, an unknown viewer) gets neutral copy. */
export const seesProviders = (me: Viewer): boolean => me?.user?.platformRole === 'operator';

/** Phrases that name a provider and their neutral wording, most specific first (case-sensitive on purpose). */
const NEUTRAL: [RegExp, string][] = [
  [/https?:\/\/(?:[\w-]+\.)*(?:expo\.dev|bitrise\.io)\S*/g, 'the build service'],
  [/\b(?:[\w-]+\.)*(?:expo\.dev|bitrise\.io)\b/g, 'the build service'],
  [/\b(?:EAS|Bitrise) build\b/gi, 'build'],
  [/\bEAS profile\b/g, 'profile in eas.json'],
  [/\bBitrise workflow\b/g, 'build workflow'],
  [/\b(?:EAS project|Bitrise app)\b/g, 'build setup'],
  [/\b(?:EAS|Bitrise|Expo) (account|workspace|organization)/g, 'build service $1'],
  [/\bExpo EAS\b/g, 'the build service'],
  [/\b(on|to|at|from|in) (?:Expo|EAS|Bitrise)\b(?!['’]s)/g, '$1 the build service'],
  [/\b(?:Expo|EAS|Bitrise)['’]s\b/g, "the build service's"],
  [/\b(?:EAS|Bitrise)\b/g, 'the build service'],
];

/**
 * A text written by the API for engineers, reworded for a client: provider names, hosts and links become "the build
 * service" ("EAS build 5218…" → "Build 5218…"). "Expo" alone stays: it is also the client's framework ("Expo apps").
 */
export function neutralWording(text: string): string {
  const out = NEUTRAL.reduce((t, [re, to]) => t.replace(re, to), text);
  return out !== text && /^[a-z]/.test(out) && /^[A-Z]/.test(text) ? out[0]!.toUpperCase() + out.slice(1) : out;
}

/** `neutralWording` for clients, the text unchanged for operators. */
export const wordingFor = (sees: boolean) => (text: string): string => (sees ? text : neutralWording(text));

let viewerSeesProviders = false;

/** The shell records who is signed in, for code outside React (error toasts). */
export function rememberAudience(me: Viewer): void {
  viewerSeesProviders = seesProviders(me);
}

/** API text for code outside React (toasts), worded for the viewer the shell last recorded. */
export const forViewer = (text: string): string => wordingFor(viewerSeesProviders)(text);
